// Recovery utilities for stuck Service Workers and orphaned Cache Storage
// entries left behind by previous app versions.
//
// Pre-shell-mode the Workbox config used StaleWhileRevalidate runtime caches
// (`api-org-data`, `api-reference-data`). The new shell-only SW is
// NetworkOnly and never reads them — but `cleanupOutdatedCaches()` only
// purges Workbox precaches, not arbitrary runtime caches. Without explicit
// cleanup these orphans live forever, and on iOS the previous SW itself can
// keep intercepting fetches for >24h after a deploy, serving an old
// `index.html` whose hashed chunks are already gone from Firebase Hosting →
// lazy `import()` rejects → ErrorBoundary catches a dynamic-import failure
// that no amount of reloading fixes.

import { SHARED_FILES_CACHE } from '@/features/expenses/utils/consumeSharedFiles'

const PRECACHE_PREFIX = 'workbox-precache'
// Active cache names that the current build owns. These survive purge.
const PROTECTED_CACHES = new Set([SHARED_FILES_CACHE])

function isOrphanedCache(name: string): boolean {
  return !name.startsWith(PRECACHE_PREFIX) && !PROTECTED_CACHES.has(name)
}

/**
 * Delete every Cache Storage entry that isn't owned by the current Workbox
 * precache. Safe to call on every boot — typical cost is one `caches.keys()`
 * with zero deletions once the device has caught up.
 *
 * Returns the names that were deleted (mostly for tests / observability).
 */
export async function purgeOrphanedCaches(
  cacheStorage: CacheStorage | undefined = typeof caches !== 'undefined'
    ? caches
    : undefined,
): Promise<string[]> {
  if (!cacheStorage) return []
  try {
    const names = await cacheStorage.keys()
    const orphans = names.filter(isOrphanedCache)
    if (orphans.length === 0) return []
    await Promise.all(orphans.map((name) => cacheStorage.delete(name)))
    return orphans
  } catch {
    return []
  }
}

/**
 * Last-resort recovery for users stuck on an old Service Worker that keeps
 * intercepting fetches with a stale `index.html` referencing deleted JS
 * chunks. Unregisters every SW, wipes every cache, then reloads.
 *
 * Triggered manually from the ErrorBoundary's secondary button — never
 * automatically, because it tears down everything the SW was doing.
 */
export async function hardReset(
  reload: () => void = () => {
    window.location.reload()
  },
): Promise<void> {
  if (typeof caches !== 'undefined') {
    try {
      const names = await caches.keys()
      await Promise.all(names.map((name) => caches.delete(name)))
    } catch {
      /* best-effort */
    }
  }
  if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
    try {
      const regs = await navigator.serviceWorker.getRegistrations()
      await Promise.all(regs.map((reg) => reg.unregister()))
    } catch {
      /* best-effort */
    }
  }
  reload()
}
